import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Upload, HandHeart, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MemberLayout from "../components/MemberLayout";
import { api } from "../lib/api";
import { formatApiError } from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";

const CATEGORIES = [
  { k: "medical", label: "Medical" },
  { k: "educational", label: "Educational" },
  { k: "emergency", label: "Emergency" },
  { k: "other", label: "Other" },
];

export default function MemberWelfare() {
  const { member, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { if (!loading && !member) nav("/member/login", { replace: true }); }, [loading, member, nav]);
  const load = async () => {
    setLoadingList(true);
    try { const { data } = await api.get("/member/welfare"); setItems(data); }
    finally { setLoadingList(false); }
  };
  useEffect(() => { if (member) load(); }, [member]);

  if (loading || !member) return null;

  return (
    <MemberLayout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="member-welfare-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Welfare fund</div>
            <h1 className="font-heading text-3xl md:text-4xl font-light text-ink">My applications</h1>
            <p className="text-ink-soft mt-2 max-w-xl">Apply for medical aid, educational scholarships, or emergency support from the community welfare fund.</p>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="welfare-new-btn"
          >
            <Plus className="w-4 h-4" /> {showForm ? "Close" : "New application"}
          </button>
        </div>

        {showForm && <WelfareForm onDone={() => { setShowForm(false); load(); }} />}

        <section>
          {loadingList ? (
            <div className="text-ink-muted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="card-premium text-center py-14" data-testid="welfare-empty">
              <HandHeart className="w-10 h-10 text-brand mx-auto" strokeWidth={1.5} />
              <h3 className="font-heading text-xl text-ink mt-4">No applications yet</h3>
              <p className="text-ink-soft mt-2">When you need support from the community, start a new application above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((w) => <WelfareCard key={w.id} w={w} />)}
            </div>
          )}
        </section>
      </div>
    </MemberLayout>
  );
}

function WelfareCard({ w }) {
  const toneMap = { pending: "bg-amber-50 text-amber-700", approved: "bg-green-50 text-green-700", rejected: "bg-red-50 text-red-700" };
  return (
    <article className="card-premium" data-testid={`welfare-card-${w.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">{w.category}</div>
          <h3 className="font-heading text-xl text-ink mt-1">{w.title}</h3>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${toneMap[w.status] || "bg-black/5 text-ink"}`} data-testid={`welfare-status-${w.id}`}>{w.status}</span>
      </div>
      <div className="mt-4 font-heading text-2xl font-medium text-brand">₹{w.amount.toLocaleString("en-IN")}</div>
      <p className="mt-3 text-ink-soft text-sm leading-relaxed line-clamp-3">{w.description}</p>
      {w.admin_note && (
        <div className="mt-4 p-3 rounded-xl bg-cream-deep text-sm">
          <div className="overline mb-1">Admin note</div>
          <div className="text-ink-soft">{w.admin_note}</div>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between text-xs text-ink-muted">
        <span>{formatDate(w.created_at)}</span>
        {w.document_path && <span className="inline-flex items-center gap-1 text-brand"><FileText className="w-3 h-3" /> Document attached</span>}
      </div>
    </article>
  );
}

function WelfareForm({ onDone }) {
  const [form, setForm] = useState({ category: "medical", title: "", amount: "", description: "" });
  const [docPath, setDocPath] = useState(null);
  const [docFileId, setDocFileId] = useState("");
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/member/welfare/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocPath(data.path);
      setDocFileId(data.file_id || "");
      setDocName(data.filename || f.name);
      toast.success("Document attached");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseInt(form.amount, 10);
    if (!amt || amt < 1) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post("/member/welfare", {
        category: form.category,
        title: form.title,
        amount: amt,
        description: form.description,
        document_path: docPath,
        document_file_id: docFileId,
      });
      toast.success("Application submitted");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-premium space-y-5" data-testid="welfare-form">
      <div>
        <label className="block text-sm font-medium text-ink-soft mb-2">Category</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="welfare-category-group">
          {CATEGORIES.map((c) => (
            <button
              key={c.k}
              type="button"
              onClick={() => setForm((f) => ({ ...f, category: c.k }))}
              className={`rounded-xl border px-4 py-3 text-sm font-medium capitalize transition ${form.category === c.k ? "border-brand bg-brand/5 text-brand" : "border-black/10 hover:border-brand/40 text-ink-soft"}`}
              data-testid={`welfare-category-${c.k}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">Application title</label>
          <input className="input-premium" value={form.title} onChange={change("title")} placeholder="e.g. Surgery for spouse" required data-testid="welfare-title-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">Amount requested (₹)</label>
          <input type="number" className="input-premium" value={form.amount} onChange={change("amount")} placeholder="50000" min={1} required data-testid="welfare-amount-input" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-ink-soft mb-2">Description</label>
        <textarea className="input-premium min-h-[140px]" value={form.description} onChange={change("description")} placeholder="Provide context — situation, timeline, what the funds will be used for." required minLength={10} data-testid="welfare-description-input" />
      </div>
      <div>
        <label className="block text-sm font-medium text-ink-soft mb-2">Supporting document (optional)</label>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary w-full sm:w-auto justify-center" data-testid="welfare-upload-btn">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> {docPath ? "Replace" : "Upload"}</>}
          </button>
          {docName && <span className="text-sm text-ink-soft" data-testid="welfare-doc-filename">{docName}</span>}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadDoc} data-testid="welfare-file-input" />
        </div>
        <p className="text-xs text-ink-muted mt-2">JPEG, PNG, WEBP or PDF. Up to 5 MB.</p>
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-2">
        <button type="button" onClick={onDone} className="btn-ghost w-full sm:w-auto" data-testid="welfare-cancel-btn">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary w-full sm:w-auto" data-testid="welfare-submit-btn">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit application"}
        </button>
      </div>
    </form>
  );
}

function formatDate(iso) {
  return formatDateTime(iso, "—");
}
